import React, { useCallback, useMemo, useState } from "react";
import { DragDropContext, Droppable, Draggable } from "@hello-pangea/dnd";
import type { DropResult, DraggableStyle } from "@hello-pangea/dnd";
import { useFieldArray, Controller, useWatch, useFormContext } from "react-hook-form";
import {
  Stack,
  Text,
  FormControl,
  FormLabel,
  Box,
  Button,
  Checkbox,
  Tabs,
  TabList,
  TabPanels,
  Tab,
  TabPanel,
  Badge,
  Flex,
  Input,
  FormErrorMessage,
  Circle,
  IconButton,
  Icon,
  useColorModeValue,
  Tag,
  InputGroup,
  InputRightElement,
} from "@chakra-ui/react";
import { DragHandleIcon, CloseIcon } from "@chakra-ui/icons";
import { RequirementsFieldArray } from "./RequirementsFieldArray";
import { useButtonProps } from "hooks";
import type { FormValues } from "./ConfiguredWorkflowUpsertModal";
import { IncludedEntitiesFieldArray } from "./IncludedEntitiesFieldArray";
import { EntitiesToRenameFieldArray } from "./EntitiesToRenameFieldArray";
import { EntitiesToInjectFieldArray } from "./EntitiesToInjectFieldArray";
import { CheckpointUserIntentField } from "./CheckpointUserIntentField";
import { CheckpointIntentContextProvider } from "../context/CheckpointIntentContext";
import { EntityToSplit } from "./EntityToSplit";
import type { CheckpointType as TCheckpointType } from "types/configuredWorkflows";
import { CheckpointType } from "./CheckpointType";
import { AiOutlineDelete } from "react-icons/ai";
import { ChevronDownIcon, ChevronUpIcon } from "@chakra-ui/icons";

const getItemStyle = (isDragging: boolean, draggableStyle?: DraggableStyle): React.CSSProperties => ({
  userSelect: "none",
  margin: `0`,
  boxShadow: isDragging ? "0 0 10px rgba(0,0,0,0.5)" : "none",
  ...draggableStyle,
});

export const getCheckpointColorScheme = (type: TCheckpointType): string => {
  switch (type) {
    case "execute_intent":
      return "blue";
    case "create_entities":
      return "green";
    case "milestone":
      return "purple";
    default:
      return "gray";
  }
};

export const CheckpointsFieldArray = () => {
  const [openItems, setOpenItems] = useState<Record<number, boolean>>({});
  const handlePanelToggle = useCallback((index: number, isOpen: boolean) => {
    setOpenItems((prev) => ({
      ...prev,
      [index]: isOpen,
    }));
  }, []);

  const {
    control,
    formState: { errors },
  } = useFormContext<FormValues>();
  const [search, setSearch] = React.useState<string>("");
  const commonButtonProps = useButtonProps("sm", "secondary");
  const buttonColor = useColorModeValue("gray.500", "gray.600");
  const buttonHoverColor = useColorModeValue("gray.600", "gray.400");
  const checkpointFontColor = useColorModeValue("gray.600", "gray.800");
  const checkpointBgColor = useColorModeValue("100", "300");

  const { fields, prepend, remove, move, insert } = useFieldArray({
    control,
    name: "config.checkpoints",
    keyName: "checkpointId",
  });

  const checkpoints = useWatch({
    control,
    name: "config.checkpoints",
  });

  const controlledCheckpoints = useMemo(
    () =>
      fields.map((field, index) => {
        return {
          ...field,
          ...checkpoints[index],
        };
      }),
    [checkpoints, fields]
  );

  const onDragEnd = useCallback(
    (result: DropResult) => {
      const { source, destination } = result;

      if (!destination) {
        return;
      }

      if (source.index === destination.index && source.droppableId === destination.droppableId) {
        return;
      }

      move(source.index, destination.index);
    },
    [move]
  );

  const renderExecuteIntentCheckpoint = useCallback(
    (index: number, checkpoint: any) => (
      <>
        <Stack mb="1rem" direction="row" spacing={2} alignItems="flex-end">
          <CheckpointUserIntentField index={index} />
        </Stack>
        <Stack direction="row">
          <Box flex={"1"} padding={"1rem"} mt="1rem" mb="1rem" borderWidth="1px" borderRadius="lg">
            <EntitiesToInjectFieldArray checkpointIndex={index} />
          </Box>
        </Stack>
        <Stack direction="row">
          <Controller
            render={({ field }) => (
              <Checkbox size={"sm"} isChecked={field.value} name={field.name} onChange={(e) => field.onChange(e.target.checked)}>
                Confirm before running
              </Checkbox>
            )}
            name={`config.checkpoints.${index}.needsConfirmation`}
            control={control}
          />
        </Stack>

        <Stack direction="row">
          <Controller
            render={({ field }) => (
              <Checkbox size={"sm"} isChecked={field.value} name={field.name} onChange={(e) => field.onChange(e.target.checked)}>
                Run in separate child workflow
              </Checkbox>
            )}
            name={`config.checkpoints.${index}.runInNewChildWorkflow`}
            control={control}
          />
        </Stack>
        {checkpoint.runInNewChildWorkflow && (
          <>
            <Box mt={"1rem"} mb="1rem" borderWidth="1px" borderRadius="lg">
              <Tabs isFitted>
                <TabList>
                  <Tab>
                    <Flex>
                      <Text>Entities to Restrict to Child Workflow</Text>
                      {checkpoint.onlyIncludeTheseEntities?.length ? (
                        <Badge ml={"0.5rem"}>{checkpoint.onlyIncludeTheseEntities.length}</Badge>
                      ) : undefined}
                    </Flex>
                  </Tab>
                  <Tab>
                    <Flex>
                      <Text>Entities to Rename</Text>
                      {checkpoint.entitiesToRename?.length ? <Badge ml={"0.5rem"}>{checkpoint.entitiesToRename.length}</Badge> : undefined}
                    </Flex>
                  </Tab>
                </TabList>

                <TabPanels>
                  <TabPanel>
                    <IncludedEntitiesFieldArray checkpointIndex={index} />
                  </TabPanel>
                  <TabPanel>
                    <EntitiesToRenameFieldArray checkpointIndex={index} />
                  </TabPanel>
                </TabPanels>
              </Tabs>
            </Box>
            <EntityToSplit checkpointIndex={index} />
            <Stack direction="row">
              <Controller
                render={({ field }) => (
                  <Checkbox size={"sm"} isChecked={field.value} name={field.name} onChange={(e) => field.onChange(e.target.checked)}>
                    Ignore child workflow if it fails (Don't fail entire workflow)
                  </Checkbox>
                )}
                name={`config.checkpoints.${index}.childWorkflowSuccessIsOptional`}
                control={control}
              />
            </Stack>
            <Stack direction="row">
              <Controller
                render={({ field }) => (
                  <Checkbox size={"sm"} isChecked={field.value} name={field.name} onChange={(e) => field.onChange(e.target.checked)}>
                    Do not wait for previous child workflow to complete
                  </Checkbox>
                )}
                name={`config.checkpoints.${index}.doNotWaitForPreviousChildWorkflowsToComplete`}
                control={control}
              />
            </Stack>
          </>
        )}
        <RequirementsFieldArray checkpointIndex={index} />
      </>
    ),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    []
  );

  const renderCreateEntitiesCheckpoint = useCallback(
    (index: number) => (
      <>
        <FormControl>
          <FormLabel fontSize="sm">Entities to Inject</FormLabel>
          <EntitiesToInjectFieldArray checkpointIndex={index} />
        </FormControl>
        <RequirementsFieldArray checkpointIndex={index} />
      </>
    ),
    []
  );

  const renderMilestoneCheckpoint = useCallback((index: number) => {
    const maybeCheckpointError = errors?.config?.checkpoints?.[index]?.label;
    return (
      <>
        <FormControl isInvalid={!!maybeCheckpointError}>
          <FormLabel fontSize="sm">Milestone label</FormLabel>
          <Controller
            name={`config.checkpoints.${index}.label`}
            control={control}
            render={({ field }) => <Input {...field} size={"sm"} placeholder="Milestone label" />}
          />
          {maybeCheckpointError?.message && <FormErrorMessage>{maybeCheckpointError.message}</FormErrorMessage>}
        </FormControl>
        <RequirementsFieldArray checkpointIndex={index} />
      </>
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Main renderCheckpoint function that delegates to the appropriate renderer
  const renderCheckpoint = useCallback(
    (index: number) => {
      const checkpoint = controlledCheckpoints[index];
      if (!checkpoint) return null;

      switch (checkpoint.type) {
        case "execute_intent":
          return renderExecuteIntentCheckpoint(index, checkpoint);
        case "create_entities":
          return renderCreateEntitiesCheckpoint(index);
        case "milestone":
          return renderMilestoneCheckpoint(index);
        default:
          return null;
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [controlledCheckpoints]
  );

  return (
    <DragDropContext onDragEnd={onDragEnd}>
      <Box>
        <Stack direction={"row"} alignItems="center">
          <InputGroup size="sm" mb={2}>
            <Input
              value={search}
              onChange={(evt) => setSearch(evt.target.value)}
              placeholder="Filter checkpoints"
              aria-label="Filter checkpoints"
              autoComplete="off"
            />
            {search && (
              <InputRightElement>
                <IconButton aria-label="Clear search" icon={<CloseIcon />} size="xs" variant="ghost" onClick={() => setSearch("")} />
              </InputRightElement>
            )}
          </InputGroup>
          <Box width="100%" display="flex" justifyContent="flex-end" mb={4}>
            <Button
              {...commonButtonProps}
              width="auto"
              onClick={() => {
                prepend({
                  intent: "",
                  requirements: [],
                  runInNewChildWorkflow: false,
                  needsConfirmation: false,
                  doNotWaitForPreviousChildWorkflowsToComplete: false,
                  showEntityToSplit: false,
                  entityToSplitName: "",
                  entityToSplitRenameTo: "",
                  entityToSplitShouldUnwrap: false,
                  entitiesToInject: [],
                  entitiesToRename: [],
                  type: "execute_intent",
                });
                setOpenItems((prev) => {
                  const updated = Object.entries(prev).reduce((acc, [key, value]) => {
                    acc[Number(key) + 1] = value;
                    return acc;
                  }, {} as Record<number, boolean>);

                  updated[0] = true;
                  return updated;
                });
              }}>
              Add Checkpoint
            </Button>
          </Box>
        </Stack>
        <CheckpointIntentContextProvider>
          <Droppable droppableId="checkpoints">
            {(droppableProvided, droppableSnapshot) => (
              <Stack {...droppableProvided.droppableProps} ref={droppableProvided.innerRef} spacing={4} width="100%">
                {/* Create a filtered version of fields based on search input */}
                {fields.length > 0 &&
                  (() => {
                    const filteredFields = fields.filter((field, index) => {
                      if (!search) return true;

                      const checkpoint = controlledCheckpoints[index];
                      if (!checkpoint) return false;
                      const searchLower = search.toLowerCase();
                      if (checkpoint.type === "execute_intent" && checkpoint.intent?.toLowerCase().includes(searchLower)) return true;
                      if (checkpoint.type === "milestone" && checkpoint.label?.toLowerCase().includes(searchLower)) return true;

                      return false;
                    });

                    if (search && filteredFields.length === 0) {
                      return (
                        <Box p={4} textAlign="left" borderWidth="1px" borderRadius="md">
                          <Text fontSize="md">No checkpoints found matching "{search}"</Text>
                        </Box>
                      );
                    }

                    return filteredFields.map((field, index) => (
                      <Draggable key={field.checkpointId} draggableId={field.checkpointId || String(index)} index={index}>
                        {(draggableProvided, snapshot) => (
                          <Box
                            {...draggableProvided.draggableProps}
                            ref={draggableProvided.innerRef}
                            style={getItemStyle(snapshot.isDragging, draggableProvided.draggableProps.style)}
                            left={"auto !important"}
                            id={`config.checkpoints.${index}`}
                            mb={4}>
                            <Box borderWidth="2px" borderRadius="md">
                              <Button
                                width="100%"
                                justifyContent="space-between"
                                height="auto"
                                py={2}
                                borderRadius="md"
                                variant="ghost"
                                onClick={() => {
                                  const isOpen = !openItems[index];
                                  handlePanelToggle(index, isOpen);
                                }}>
                                <Stack direction="row" width="100%" justifyContent="space-between" alignItems="center">
                                  <Flex alignItems="center">
                                    <Box {...draggableProvided.dragHandleProps} mr={2} aria-label="Drag checkpoint">
                                      <DragHandleIcon color="gray.400" />
                                    </Box>
                                    <Circle
                                      fontSize={"10px"}
                                      size="22px"
                                      fontWeight={"semibold"}
                                      color={checkpointFontColor}
                                      bgColor={`${getCheckpointColorScheme(controlledCheckpoints[index].type)}.${checkpointBgColor}`}
                                      mr={2}>
                                      {index + 1}
                                    </Circle>
                                    <Text fontSize={"md"} fontWeight="bold">
                                      {controlledCheckpoints[index].type === "execute_intent"
                                        ? field.intent
                                        : controlledCheckpoints[index].type === "milestone"
                                        ? controlledCheckpoints[index].label
                                        : controlledCheckpoints[index].type === "create_entities"
                                        ? "Create Entities"
                                        : `Checkpoint ${index + 1}`}
                                    </Text>
                                  </Flex>
                                  <Stack direction="row" alignItems="center">
                                    <Tag size="sm" colorScheme={getCheckpointColorScheme(controlledCheckpoints[index].type)} mr={2}>
                                      {controlledCheckpoints[index].type}
                                    </Tag>
                                    <IconButton
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        remove(index);
                                      }}
                                      aria-label="Delete"
                                      backgroundColor="unset"
                                      icon={
                                        <Icon
                                          as={AiOutlineDelete}
                                          color={buttonColor}
                                          boxSize="1rem"
                                          _hover={{ color: buttonHoverColor }}
                                        />
                                      }
                                      size="sm"
                                      _hover={{ backgroundColor: "unset" }}
                                    />
                                    <Icon as={openItems[index] ? ChevronUpIcon : ChevronDownIcon} boxSize="1.2rem" />
                                  </Stack>
                                </Stack>
                              </Button>

                              {openItems[index] && (
                                <Box pb={4} padding="1rem">
                                  <CheckpointType control={control} checkpointIndex={index} onRemove={() => remove(index)} />
                                  {field.id && (
                                    <Controller
                                      name={`config.checkpoints.${index}.id`}
                                      control={control}
                                      render={({ field: controllerField }) => (
                                        <FormControl>
                                          <Input {...controllerField} type="hidden" />
                                        </FormControl>
                                      )}
                                    />
                                  )}

                                  {renderCheckpoint(index)}

                                  <Stack direction="row" spacing="1rem" mt="1rem">
                                    <Button
                                      {...commonButtonProps}
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        insert(index + 1, {
                                          intent: "",
                                          requirements: [],
                                          runInNewChildWorkflow: false,
                                          needsConfirmation: false,
                                          doNotWaitForPreviousChildWorkflowsToComplete: false,
                                          showEntityToSplit: false,
                                          entityToSplitName: "",
                                          entityToSplitRenameTo: "",
                                          entityToSplitShouldUnwrap: false,
                                          entitiesToInject: [],
                                          entitiesToRename: [],
                                          type: "execute_intent",
                                        });
                                      }}>
                                      Add new checkpoint after this one
                                    </Button>
                                    <Button
                                      {...commonButtonProps}
                                      ml={"0.5"}
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        insert(index + 1, { ...field, id: undefined });
                                      }}>
                                      Duplicate checkpoint
                                    </Button>
                                  </Stack>
                                </Box>
                              )}
                            </Box>
                          </Box>
                        )}
                      </Draggable>
                    ));
                  })()}
                {droppableProvided.placeholder}
              </Stack>
            )}
          </Droppable>
        </CheckpointIntentContextProvider>
      </Box>
    </DragDropContext>
  );
};
